import { useState, Fragment } from "react";
import { LenderPolicy, policyCompletion, deriveProductType, normalizeLenderPolicy } from "../../types/lender";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Pencil, Copy, Trash2, Plus, Loader2 } from "lucide-react";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { cn } from "../../lib/utils";

interface PolicyTableProps {
  lenderId: string;
  policies: LenderPolicy[];
  onAdd: () => void;
  onEdit: (policy: LenderPolicy) => void;
  onDuplicated?: () => void | Promise<void>;
  onDeleted?: () => void | Promise<void>;
  onStatusChanged?: () => void | Promise<void>;
}

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function duplicatePolicyName(name: string) {
  const base = name.trim();
  return base.endsWith(" (copy)") ? base : `${base} (copy)`;
}

export function PolicyTable({ lenderId, policies, onAdd, onEdit, onDuplicated, onDeleted, onStatusChanged }: PolicyTableProps) {
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleDuplicate = async (policy: LenderPolicy) => {
    if (duplicatingId || deletingId || togglingId) return;

    const normalized = normalizeLenderPolicy(policy);
    setDuplicatingId(normalized.id);

    try {
      await apiService.post("/lender/create-lender-policy", {
        lenderId,
        id: null,
        name: duplicatePolicyName(normalized.name),
        preferences: normalized.preferences,
        active: false,
      });
      toast.success("Policy duplicated successfully!");
      await onDuplicated?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to duplicate policy. Please try again.");
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (policyId: string) => {
    if (deletingId) return;

    setDeletingId(policyId);
    try {
      await apiService.post("/lender/delete-lender-policy", { id: policyId });
      toast.success("Policy deleted successfully!");
      await onDeleted?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete policy. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (policyId: string, active: boolean) => {
    if (togglingId || duplicatingId || deletingId) return;

    setTogglingId(policyId);
    try {
      await apiService.post("/lender/set-policy-active-status", { id: policyId, active });
      toast.success(`Policy ${active ? "activated" : "deactivated"} successfully!`);
      await onStatusChanged?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update policy status. Please try again.");
    } finally {
      setTogglingId(null);
    }
  };

  const isBusy = !!duplicatingId || !!deletingId || !!togglingId;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {policies.length} {policies.length === 1 ? "policy" : "policies"}
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5" /> Add Investment Preference
        </Button>
      </div>

      {policies.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-white px-4 py-6 text-center">
          <p className="text-sm font-medium text-gray-600">No Policies yet</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-medium px-3 py-2">Policy Name</th>
                <th className="text-left font-medium px-3 py-2">Product Type</th>
                <th className="text-left font-medium px-3 py-2 w-[180px]">Completion</th>
                <th className="text-left font-medium px-3 py-2 w-[100px]">Status</th>
                <th className="text-left font-medium px-3 py-2">Updated By</th>
                <th className="text-left font-medium px-3 py-2">Last Updated</th>
                <th className="text-right font-medium px-3 py-2 w-[120px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              <TooltipProvider>
                {policies?.map(rawPolicy => {
                  const p = normalizeLenderPolicy(rawPolicy);
                  const completion = policyCompletion(p.preferences);
                  const isDuplicating = duplicatingId === p.id;
                  const isDeleting = deletingId === p.id;
                  const isToggling = togglingId === p.id;

                  return (
                    <Fragment key={p.id}>
                      <tr className={cn("border-b hover:bg-muted/20", isDuplicating && "bg-primary/5")}>
                        <td className="px-3 py-2.5 font-medium">{p.name}</td>
                        <td className="px-3 py-2.5">
                          <Badge variant="outline" className="text-[10px] h-5">{deriveProductType(p.preferences)}</Badge>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <Progress value={completion} className="h-1.5 flex-1" />
                            <span className="text-xs font-medium w-9 text-right">{completion}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={p.active}
                              onCheckedChange={(v) => handleToggleActive(p.id, v)}
                              className="scale-90"
                              disabled={isBusy}
                            />
                            {isToggling ? (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            ) : null}
                            <span className={`text-[10px] font-medium ${p.active ? "text-[hsl(var(--status-success))]" : "text-muted-foreground"}`}>
                              {p.active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{p.updatedBy}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{formatDate(p.updatedAt || p.createdAt)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(p)} disabled={isBusy}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => handleDuplicate(p)}
                                  disabled={isBusy}
                                >
                                  {isDuplicating ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Duplicate</TooltipContent>
                            </Tooltip>
                            <AlertDialog>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-red-600 hover:bg-red-50 hover:text-red-700"
                                      disabled={isBusy}
                                    >
                                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                    </Button>
                                  </AlertDialogTrigger>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                              <AlertDialogContent className="bg-white border border-neutral-200 rounded-xl shadow-xl sm:max-w-md">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-gray-900">Delete "{p.name}"?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-gray-600">
                                    This policy will be permanently removed and will no longer participate in AI matching.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="gap-2 sm:gap-0">
                                  <AlertDialogCancel className="bg-white border-neutral-300 text-gray-700 hover:bg-gray-50 mt-0">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(p.id)}
                                    disabled={isDeleting}
                                    className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
                                  >
                                    {isDeleting ? (
                                      <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        Deleting...
                                      </>
                                    ) : (
                                      "OK"
                                    )}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                      {isDuplicating && (
                        <tr className="border-b last:border-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
                          <td colSpan={7} className="px-3 py-0">
                            <div className="flex items-center gap-3 py-2.5 pl-1">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground">Creating policy...</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  Duplicating as "{duplicatePolicyName(p.name)}"
                                </p>
                              </div>
                              <div className="ml-auto h-1 flex-1 max-w-[120px] overflow-hidden rounded-full bg-primary/10">
                                <div className="h-full w-1/2 animate-pulse rounded-full bg-primary/40" />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </TooltipProvider>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
